import { Box, Group, Paper, Select, SimpleGrid, Stack, Text, Button, Flex } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";
import AppHeaderUserActions from "@/components/shared/AppHeaderUserActions";
import DashboardCard from "./DashboardCard";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { notifications } from "@mantine/notifications";
import CollaborationTimelineCard, {
  CollaborationTimelineCardSkeleton,
  type CollaborationTimelineCardItem,
} from "./CollaborationTimelineCard";
import TitleComponent from "@/components/shared/TitleComponent";
import ApplyVerificationModal from "@/pages/profile-verification/ApplyVerificationModal";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { userDetails, pendingRequestCounts, collaborationSidebarCounts, shortlistedIds, refetchUser } = useUserContext();
  const userName = userDetails?.name?.trim() || "User";
  const [timelineItems, setTimelineItems] = useState<CollaborationTimelineCardItem[]>([]);
  const [selectedStage, setSelectedStage] = useState<string>("all");
  const [selectedProgress, setSelectedProgress] = useState<string>("pending");
  const [selectedScope, setSelectedScope] = useState<string>("your");
  const [companyDetails, setCompanyDetails] = useState<any>(null);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [verificationData, setVerificationData] = useState<Record<string, unknown> | null>(null);
  const [verificationApplying, setVerificationApplying] = useState(false);

  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const companyStatus = userDetails?.company?.status;
  const canShowVerifyButton = companyStatus !== "VERIFIED" && companyStatus !== "SYSTEM_VERIFIED";

  const filteredTimelineItems = useMemo(() => {
    if (selectedStage === "all") return timelineItems;

    const getStepDone = (item: CollaborationTimelineCardItem, side: "mySide" | "partnerSide"): boolean => {
      const sideSteps = item[side];
      if (selectedStage === "request") return sideSteps.request.done;
      if (selectedStage === "payment") return sideSteps.payment.done;
      if (selectedStage === "nda") return sideSteps.nda.done;
      return sideSteps.partnerReadiness.done;
    };

    return timelineItems.filter((item) => {
      const isDoneMySide = getStepDone(item, "mySide");
      const isDonePartnerSide = getStepDone(item, "partnerSide");
      const targetDone = selectedProgress === "completed";

      if (selectedScope === "your") return isDoneMySide === targetDone;
      if (selectedScope === "partner") return isDonePartnerSide === targetDone;
      return isDoneMySide === targetDone && isDonePartnerSide === targetDone;
    });
  }, [timelineItems, selectedStage, selectedProgress, selectedScope]);

  useEffect(() => {
    let mounted = true;

    const fetchTimeline = async () => {
      setLoadingTimeline(true);
      try {
        const data = await trpc.collaboration.getDashboardTimeline.query();
        if (!mounted) return;
        setTimelineItems((data || []) as CollaborationTimelineCardItem[]);
      } catch (err) {
        handleTrpcError(err);
        if (!mounted) return;
        setTimelineItems([]);
      } finally {
        if (mounted) setLoadingTimeline(false);
      }
    };

    if (userDetails?.company?.id) {
      fetchTimeline();
    } else {
      setLoadingTimeline(false);
      setTimelineItems([]);
    }

    return () => {
      mounted = false;
    };
  }, [userDetails?.company?.id]);

  useEffect(() => {
    let mounted = true;
    const companyId = userDetails?.company?.id;
    if (!companyId) {
      setCompanyDetails(null);
      return;
    }
    const fetchCompany = async () => {
      try {
        const data = await trpc.company.getById.query({ companyId });
        if (mounted) setCompanyDetails(data);
      } catch (err) {
        if (mounted) setCompanyDetails(null);
      }
    };
    void fetchCompany();
    return () => {
      mounted = false;
    };
  }, [userDetails?.company?.id]);

  const handleCloseVerificationModal = () => {
    setVerificationModalOpen(false);
    setVerificationData(null);
  };

  const handleApplyVerification = async (payload: { registrationIdType: string; registrationNumber: string; }) => {
    const companyId = userDetails?.company?.id;
    if (!companyId || !companyDetails) return;
    setVerificationApplying(true);
    try {
      const hqTrim = (companyDetails.headquartersCountry || "")?.trim() ?? "";
      const usesLei = payload.registrationIdType === "LEI";
      const usesUsccShape =
        payload.registrationIdType === "USCC" ||
        (payload.registrationIdType === "COUNTRY_REGISTRATION" && hqTrim === "China (Mainland)");
      const usesDigitsOnlyType =
        payload.registrationIdType === "NOGN" || payload.registrationIdType === "SIREN";
      const normalizedRegistrationNumber = usesLei || usesUsccShape
        ? payload.registrationNumber.trim().toUpperCase()
        : usesDigitsOnlyType
          ? payload.registrationNumber.replace(/\D/g, "")
          : payload.registrationNumber.trim();

      await trpc.company.update.mutate({
        companyId,
        data: {
          registrationIdType: payload.registrationIdType,
          registrationNumber: normalizedRegistrationNumber || null,
        },
      });

      const result = await trpc.verification.verifyCompany.mutate({
        legalName: companyDetails.legalName,
        registrationNumber: normalizedRegistrationNumber,
        headquartersCountry: companyDetails.headquartersCountry,
        state: companyDetails.state || undefined,
      });

      if (result?.success && "data" in result && result.data) {
        const data = result.data as Record<string, unknown>;
        const nextVerificationData = {
          ...data,
          _verificationSource: (data.verificationSource as string | undefined) ?? result.source,
        };
        setVerificationData(nextVerificationData);
        localStorage.setItem(
          "my-profile:last-system-verification",
          JSON.stringify({
            companyId,
            data: nextVerificationData,
          })
        );
        await refetchUser();
      } else if (result && !result.success && "pending24h" in result && result.pending24h) {
        notifications.show({
          title: "Verification pending admin review",
          message:
            (result as { message?: string }).message ||
            "Please wait up to 24 hours. Our team will review your registration. You will be notified when your profile is verified.",
          color: "blue",
        });
        await refetchUser();
        handleCloseVerificationModal();
      }
    } catch (err) {
      handleTrpcError(err);
      handleCloseVerificationModal();
    } finally {
      setVerificationApplying(false);
    }
  };

  return (
    <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
      <TitleComponent
        title={`Welcome! ${userName}`}
        description="Here is your dashboard overview."
        rightSection={<AppHeaderUserActions />}
      />

      <Box p="lg">
        <Stack gap="lg">
          {canShowVerifyButton && (
            <Paper p="md" radius="md" withBorder>
              <Flex justify="space-between" align="center" gap="sm" wrap="wrap">
                <Box>
                  <Text fw={700}>Complete Company Verification</Text>
                  <Text size="sm" c="dimmed">
                    Verify your company details from dashboard. Credits are deducted only when verification is processed.
                  </Text>
                </Box>
                <Button onClick={() => setVerificationModalOpen(true)}>Verify Company</Button>
              </Flex>
            </Paper>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md">
            <Box>
              <DashboardCard
                title="Shortlist"
                count={shortlistedIds.size}
                description="Companies you have shortlisted"
                onClick={() => navigate("/shortlist")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Sent"
                count={pendingRequestCounts.sent}
                description="Requests you've sent waiting for response"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Received"
                count={pendingRequestCounts.received}
                description="Requests you've received from others"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Active Collaborations"
                count={collaborationSidebarCounts.active}
                description="Ongoing collaborations with partners"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Closed Collaborations"
                count={collaborationSidebarCounts.closed}
                description="Completed or inactive collaborations"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
          </SimpleGrid>

          <Paper p="lg" radius="md" withBorder>
            <Group justify="space-between" align="center" mb="sm">
              <Box>
                <Text fw={700} fz="lg">
                  Collaboration Timeline
                </Text>
                <Text c="dimmed" size="sm">
                  Track the progress of your collaboration steps from both sides.
                </Text>
              </Box>
              <Group gap="lg">
                <Group gap={6}>
                  <Box w={10} h={10} style={{ borderRadius: "50%", backgroundColor: "var(--mantine-color-green-6)" }} />
                  <Text size="sm" c="dimmed">
                    Completed
                  </Text>
                </Group>
                <Group gap={6}>
                  <Box w={10} h={10} style={{ borderRadius: "50%", backgroundColor: "#E17100" }} />
                  <Text size="sm" c="dimmed">
                    Pending
                  </Text>
                </Group>
              </Group>
            </Group>

            <Group gap="sm" mb="sm" align="end">
              <Select
                label="Stage"
                data={[
                  { value: "all", label: "All Stages" },
                  { value: "request", label: "Request" },
                  { value: "payment", label: "Payment" },
                  { value: "nda", label: "NDA Sign" },
                  { value: "partnerReadiness", label: "Partner Readiness" },
                ]}
                value={selectedStage}
                onChange={(value) => setSelectedStage(value || "all")}
                w={190}
                size="xs"
              />
              <Select
                label="Progress"
                data={[
                  { value: "pending", label: "Pending" },
                  { value: "completed", label: "Completed" },
                ]}
                value={selectedProgress}
                onChange={(value) => setSelectedProgress(value || "pending")}
                w={140}
                size="xs"
              />
              <Select
                label="Scope"
                data={[
                  { value: "your", label: "Your Side" },
                  { value: "partner", label: "Partner Side" },
                  { value: "both", label: "Both Sides" },
                ]}
                value={selectedScope}
                onChange={(value) => setSelectedScope(value || "your")}
                w={150}
                size="xs"
              />
            </Group>

            {loadingTimeline ? (
              <Stack gap="sm">
                <CollaborationTimelineCardSkeleton />
              </Stack>
            ) : filteredTimelineItems.length === 0 ? (
              <Text size="sm" c="dimmed">
                No collaborations found for selected filters.
              </Text>
            ) : (
              <Stack gap="sm">
                {filteredTimelineItems.map((item) => (
                  <CollaborationTimelineCard key={item.matchId} item={item} />
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      </Box>
      <ApplyVerificationModal
        opened={verificationModalOpen}
        onClose={handleCloseVerificationModal}
        onApply={handleApplyVerification}
        isLoading={verificationApplying}
        verificationData={verificationData}
        headquartersCountry={companyDetails?.headquartersCountry || ""}
        initialRegistrationIdType={companyDetails?.registrationIdType || null}
        initialRegistrationNumber={companyDetails?.registrationNumber || ""}
      />
    </Box>
  );
}
